import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Input, Checkbox, Typography, Space, Alert, message, Tag
} from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import API from '../api';

const { Title, Text } = Typography;

const LABELS: Record<string, string> = {
  SAFETY_FEE:                        'Trust & Safety Fee (R)',
  COMMISSION_BASIC:                  'Basic Tier Commission (%)',
  COMMISSION_PRO:                    'Pro Tier Commission (%)',
  COMMISSION_BUSINESS:               'Business Tier Commission (%)',
  NFC_PLATFORM_FEE:                  'NFC Platform Fee (%)',
  IKHOKHA_CARD_FEE:                  'iKhokha Card Processing Fee (%)',
  TAP_CREDITS_LEAD_FEE:              'Cash Job Lead Fee (R)',
  MIN_PAYOUT_THRESHOLD:              'Minimum Payout Threshold (R)',
  TAP_CREDITS_LOW_BALANCE_THRESHOLD: 'Low Balance Warning Threshold (R)',
  TAP_CREDITS_BLOCK_THRESHOLD:       'Block Accept Threshold (R)',
};

function validate(key: string, raw: string): string | null {
  const v = parseFloat(raw);
  if (isNaN(v) || v < 0) return 'Value must be a non-negative number.';
  if (key === 'SAFETY_FEE' && v > 500) return 'Max R500.';
  if (['COMMISSION_BASIC', 'COMMISSION_PRO', 'COMMISSION_BUSINESS'].includes(key) && v > 1)
    return 'Commission must be between 0.00 and 1.00.';
  if (key === 'NFC_PLATFORM_FEE' && v > 0.10) return 'Max 0.10 (10%).';
  if (key === 'MIN_PAYOUT_THRESHOLD' && v < 50) return 'Minimum R50.';
  return null;
}

export default function FeeConfiguration() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [newValue, setNewValue] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/config')
      .then(r => setConfigs(r.data))
      .catch(() => message.error('Failed to load fee configuration'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = (record: any) => {
    setEditTarget(record);
    setNewValue(record.configValue);
    setConfirmed(false);
    setValidationError(null);
  };

  const handleSave = async () => {
    const err = validate(editTarget.configKey, newValue);
    if (err) { setValidationError(err); return; }
    if (!confirmed) { setValidationError('Please confirm the checkbox.'); return; }
    setSaving(true);
    try {
      await API.put(`/config/${editTarget.configKey}`, { value: newValue });
      message.success('Fee updated successfully');
      setEditTarget(null);
      load();
    } catch {
      message.error('Failed to update fee');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetOpen(false);
    setLoading(true);
    try {
      await API.post('/config/reset');
      message.success('All fees reset to defaults');
      load();
    } catch {
      message.error('Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Fee Name',
      dataIndex: 'configKey',
      render: (k: string) => LABELS[k] ?? k,
    },
    {
      title: 'Config Key',
      dataIndex: 'configKey',
      render: (k: string) => (
        <Text code style={{ color: '#6b7280', fontSize: 12 }}>{k}</Text>
      ),
    },
    {
      title: 'Current Value',
      dataIndex: 'configValue',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    { title: 'Description', dataIndex: 'description' },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      render: (d: string, r: any) =>
        d ? (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{new Date(d).toLocaleDateString()}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{r.updatedBy}</Text>
          </Space>
        ) : '—',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button size="small" icon={<SettingOutlined />} onClick={() => openEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 8, justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Fee Configuration</Title>
          <Text type="secondary">Changes take effect immediately. No redeployment needed.</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          danger
          onClick={() => setResetOpen(true)}
        >
          Reset to Defaults
        </Button>
      </Space>

      <Alert
        type="warning"
        showIcon
        message="Changing fees affects all new transactions. Existing bookings are not affected."
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={columns}
        dataSource={configs}
        rowKey="configKey"
        loading={loading}
        pagination={false}
      />

      {/* Edit modal */}
      <Modal
        title={editTarget ? `Edit — ${LABELS[editTarget.configKey] ?? editTarget.configKey}` : ''}
        open={!!editTarget}
        onCancel={() => setEditTarget(null)}
        onOk={handleSave}
        okText="Save"
        confirmLoading={saving}
        okButtonProps={{ disabled: !confirmed }}
      >
        {editTarget && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Current value: </Text>
              <Text strong>{editTarget.configValue}</Text>
            </div>
            <Input
              value={newValue}
              onChange={e => { setNewValue(e.target.value); setValidationError(null); }}
              placeholder="New value"
              status={validationError ? 'error' : ''}
            />
            {validationError && <Text type="danger">{validationError}</Text>}
            <Checkbox
              checked={confirmed}
              onChange={e => { setConfirmed(e.target.checked); setValidationError(null); }}
            >
              I understand this affects all new transactions immediately
            </Checkbox>
          </Space>
        )}
      </Modal>

      {/* Reset confirmation modal */}
      <Modal
        title="Reset all fees to defaults?"
        open={resetOpen}
        onCancel={() => setResetOpen(false)}
        onOk={handleReset}
        okText="Yes, reset"
        okButtonProps={{ danger: true }}
      >
        <Text>This will restore all platform fees to their original default values. This cannot be undone.</Text>
      </Modal>
    </div>
  );
}
